<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import InboxEvolutionAPI from 'dashboard/api/inbox/evolution';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const { t } = useI18n();
const store = useStore();

const pollTimer = ref(null);
const statusByInboxId = ref({});
const loadingByInboxId = ref({});
const reconnectingByInboxId = ref({});
const isBootstrapping = ref(false);

const allInboxes = computed(() => store.getters['inboxes/getAllInboxes'] || []);
const uiFlags = computed(() => store.getters['inboxes/getUIFlags'] || {});

const evolutionInboxes = computed(() =>
  allInboxes.value.filter(
    inbox =>
      inbox.channelType === INBOX_TYPES.WHATSAPP && inbox.provider === 'evolution'
  )
);

const isLoading = computed(
  () =>
    isBootstrapping.value ||
    (uiFlags.value.isFetching && !allInboxes.value.length)
);

const sortedEvolutionInboxes = computed(() =>
  [...evolutionInboxes.value].sort((left, right) =>
    (left.name || '').localeCompare(right.name || '')
  )
);

const getSessionState = inboxId =>
  statusByInboxId.value[inboxId]?.state || 'unknown';

const getSessionQrCode = inboxId =>
  statusByInboxId.value[inboxId]?.qrCode || '';

const stateBadgeClass = state => {
  if (state === 'open' || state === 'connected') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }

  if (state === 'connecting') {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }

  return 'bg-n-slate-3 text-n-slate-12 border-n-weak';
};

const normalizeQrCode = async rawQr => {
  if (!rawQr || typeof rawQr !== 'string') {
    return '';
  }

  if (rawQr.startsWith('data:image')) {
    return rawQr;
  }

  if (rawQr.startsWith('http')) {
    return rawQr;
  }

  if (/^[A-Za-z0-9+/=]+$/.test(rawQr)) {
    return `data:image/png;base64,${rawQr}`;
  }

  try {
    return await QRCode.toDataURL(rawQr);
  } catch {
    return '';
  }
};

const applyStatus = async (inboxId, payload = {}) => {
  const previousState = statusByInboxId.value[inboxId] || {};
  const normalizedQrCode = await normalizeQrCode(payload.qr_code);

  statusByInboxId.value = {
    ...statusByInboxId.value,
    [inboxId]: {
      state: payload.state || 'unknown',
      qrCode: normalizedQrCode || previousState.qrCode || '',
    },
  };
};

const fetchStatus = async inboxId => {
  loadingByInboxId.value = {
    ...loadingByInboxId.value,
    [inboxId]: true,
  };

  try {
    const response = await InboxEvolutionAPI.status(inboxId);
    await applyStatus(inboxId, response?.data?.data || {});
  } catch {
    await applyStatus(inboxId, { state: 'disconnected', qr_code: '' });
  } finally {
    loadingByInboxId.value = {
      ...loadingByInboxId.value,
      [inboxId]: false,
    };
  }
};

const fetchAllStatuses = async () => {
  const inboxes = sortedEvolutionInboxes.value;

  if (!inboxes.length) {
    return;
  }

  await Promise.all(inboxes.map(inbox => fetchStatus(inbox.id)));
};

const reconnectInbox = async inboxId => {
  reconnectingByInboxId.value = {
    ...reconnectingByInboxId.value,
    [inboxId]: true,
  };

  try {
    const response = await InboxEvolutionAPI.reconnect(inboxId);
    await applyStatus(inboxId, response?.data?.data || {});
    useAlert(t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.RECONNECT_SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.RECONNECT_ERROR'));
  } finally {
    reconnectingByInboxId.value = {
      ...reconnectingByInboxId.value,
      [inboxId]: false,
    };
  }
};

const stopPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

const startPolling = () => {
  stopPolling();
  pollTimer.value = setInterval(() => {
    fetchAllStatuses();
  }, 5000);
};

const initializePage = async () => {
  isBootstrapping.value = true;

  try {
    await store.dispatch('inboxes/get');
    await fetchAllStatuses();
    startPolling();
  } finally {
    isBootstrapping.value = false;
  }
};

onMounted(() => {
  initializePage();
});

onBeforeUnmount(() => {
  stopPolling();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="!isLoading && !sortedEvolutionInboxes.length"
    :no-records-message="t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.EMPTY_MESSAGE')"
    class="w-full"
  >
    <template #header>
      <div class="flex flex-col w-full max-w-5xl ltr:mr-auto rtl:ml-auto">
        <BaseSettingsHeader
          :title="t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.TITLE')"
          :description="t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.DESCRIPTION')"
        />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col w-full max-w-5xl gap-4 ltr:mr-auto rtl:ml-auto">
        <div
          v-for="inbox in sortedEvolutionInboxes"
          :key="inbox.id"
          class="grid gap-6 p-6 border rounded-2xl border-n-weak bg-n-surface-2 md:grid-cols-[minmax(0,1fr)_240px]"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-3">
              <h2 class="mb-0 text-xl font-semibold text-n-slate-12">
                {{ inbox.name }}
              </h2>
              <span
                class="inline-flex items-center px-3 py-1 text-xs font-medium border rounded-full"
                :class="stateBadgeClass(getSessionState(inbox.id))"
              >
                {{
                  t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.STATE_LABEL', {
                    state: getSessionState(inbox.id),
                  })
                }}
              </span>
            </div>

            <p class="mt-2 mb-0 text-sm text-n-slate-11">
              {{
                inbox.phoneNumber ||
                t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.NO_PHONE_NUMBER')
              }}
            </p>

            <p class="mt-4 mb-0 text-sm text-n-slate-11">
              {{ t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.HELP_TEXT') }}
            </p>

            <div class="flex flex-wrap gap-3 mt-5">
              <NextButton
                :is-loading="
                  reconnectingByInboxId[inbox.id] || loadingByInboxId[inbox.id]
                "
                @click="reconnectInbox(inbox.id)"
              >
                {{
                  getSessionQrCode(inbox.id)
                    ? t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.REFRESH_QR')
                    : t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.GENERATE_QR')
                }}
              </NextButton>

              <router-link
                :to="{
                  name: 'settings_inbox_show',
                  params: { inboxId: inbox.id, tab: 'configuration' },
                }"
              >
                <NextButton color="slate" variant="outline">
                  {{ t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.OPEN_INBOX') }}
                </NextButton>
              </router-link>
            </div>
          </div>

          <div
            class="flex flex-col items-center justify-center gap-3 p-4 border rounded-2xl border-n-weak bg-n-surface-1"
          >
            <template v-if="getSessionQrCode(inbox.id)">
              <img
                :src="getSessionQrCode(inbox.id)"
                :alt="t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.QR_ALT')"
                class="object-contain w-52 h-52 p-2 bg-white border rounded-xl border-n-weak"
              />
              <p class="mb-0 text-xs text-center text-n-slate-11">
                {{ t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.QR_HELP') }}
              </p>
            </template>

            <EmptyState
              v-else
              :title="t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.NO_QR_TITLE')"
              :message="t('GENERAL_SETTINGS.EVOLUTION_SESSIONS.NO_QR_MESSAGE')"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
